<template>
  <div class="card-item" :class="{noDoubleShadow: !hasSiblings}">
    <div class="fee-item-info clearfix" :class="{ client: currentUserIsClient, client: !collectClientDebt }">
      <div v-if="currentUserIsClient || !collectClientDebt" class="fee-item-select">
        <div v-if="isPaymentLoading" class="round-choice-transparent">
          <span class="content-title fas fa-lg fa-spinner fa-spin" />
        </div>
        <template v-else>
          <div v-if="!payment.isPending" class="round-choice-content">
            <input
              :id="`checkbox-${payment.operationId}-${payment.paymentNumber}`"
              v-model="isChecked"
              type="checkbox"
              class="input-choice"
              :disabled="disabled"
              @click.prevent="confirmCheck">
            <label
              :for="`checkbox-${payment.operationId}-${payment.paymentNumber}`"
              class="label-choice transition-fast" />
          </div>
          <div v-else>
            <b-tooltip type="is-white" label="Pago en revisión">
              <div class="round-choice-content payment-is-pending">
                <input
                  :id="`checkbox-${payment.operationId}-${payment.paymentNumber}`"
                  type="checkbox"
                  class="input-choice"
                  :checked="true"
                  :disabled="true">
                <label class="label-choice transition-fast" />
              </div>
            </b-tooltip>
          </div>
        </template>
      </div>
      <div class="fee-item-resume-content">
        <div class="type-content">
          <b-tooltip
            :label="`${$t('credit')} ${$t(type.toLowerCase())}`"
            size="is-small"
            type="is-white"
            multilined>
            <span v-if="type === 'MORTGAGE'">🏡</span>
            <span v-if="type === 'COMPLEMENTARY'">💰</span>
            <span v-if="type === 'SME'">🚀</span>
            <span v-if="type === 'PARKING'">🅿️</span>
          </b-tooltip>
        </div>
        <div class="price-content">
          <div v-if="!showAmount && !disabled" class="show-fee-label big-size-text">
            <label
              :for="`checkbox-${payment.operationId}-${payment.paymentNumber}`"
              class="is-clickable content-title">
              <span>Ver cuota</span>
            </label>
          </div>
          <div v-else-if="showAmount && !disabled || checked || payment.isPending" class="big-size-text">
            <span
              :id="`${payment.operationId}-${payment.paymentNumber}-amount`"
              class="content-title">
              {{ `${ $i18n.locale === 'cl' ? '$' : monetarySign} ${formatNumber(payment.secondaryCurrencyTotalMonthlyPayment || payment.totalMonthlyPayment)}` }}
            </span>
          </div>
          <div v-else />
        </div>
        <div v-if="!hasSiblings" class="duration-content med-size-text">
          <div>
            <span class="content-title">{{ $t('fee') }}</span>
            <span class="content-value">{{ payment.paymentNumber }}<span v-if="!currentUserIsClient && !hasSiblings && collectClientDebt"> de {{ operation.termAsMonths }}</span></span>
          </div>
          <div>
            <span :class="payment.lateDays > 0 ? 'alert-content-title' : 'content-title'">
              {{ payment.lateDays > 0 ? $t('expired') : $t('expires') }}
            </span>
            <span class="content-value">{{ dateToClientFormat(payment.dueDate) }}</span>
          </div>
        </div>
        <div v-if="!currentUserIsClient && !hasSiblings && collectClientDebt" class="late-days-content med-size-text">
          <b-tooltip :label="`${$t('lateDays')}`" type="is-white">
            <div
              class="tag med-size-text is-danger"
              :class="
                payment.lateDays >= 90 ? 'ninety-days' :
                payment.lateDays >= 60 ? 'sixty-days' :
                payment.lateDays >= 30 ? 'thirty-days' :
                payment.lateDays >= 11 ? 'less-than-thirty-days' : 'healthy-days'
              ">
              {{ payment.lateDays }}
            </div>
          </b-tooltip>
        </div>
      </div>
      <div v-if="showAmount" class="details-content">
        <a :id="`extend-${payment.operationId}-${payment.paymentNumber}`" href="#" @click.prevent="$emit('extend-payment')">
          <span class="fa" :class="isExtended ? 'fa-minus-circle' : 'fa-plus-circle'" />
        </a>
      </div>
    </div>
    <SinglePaymentDetails
      :is-extended="isExtended"
      :show-remove-late-charges="showRemoveLateCharges"
      :payment="payment"
      :operation="operation"
      :type="type"
      :monetary-sign="monetarySign"
      @remove-late-charges="$emit('remove-late-charges',$event)"
      @undo-remove-late-charges="$emit('undo-remove-late-charges',$event)" />
  </div>
</template>

<script>
import SinglePaymentDetails from '@/ui/components/internal/pay-here/SinglePaymentDetails.vue';
import { dateToClientFormat } from '@/infrastructure/date';
import { formatNumber } from '@/infrastructure/format';

export default {
  components: { SinglePaymentDetails },
  props: {
    showRemoveLateCharges: { type: Boolean, default: false },
    hasSiblings: { type: Boolean, default: false },
    isPaymentLoading: { type: Boolean, default: false },
    payment: { type: Object, default: () => ({}) },
    operation: { type: Object, default: () => ({}) },
    type: { type: String, default: '' },
    isExtended: { type: Boolean, default: false },
    checked: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    currentUserIsClient: { Boolean, default: false },
    collectClientDebt: { Boolean, default: false }
  },
  data() {
    return {};
  },
  computed: {
    isChecked: {
      // return this.checked;
      get() {
        return this.checked;
      },
      // Se comenta set porque
      // 1) No está testeado
      // 2) No hace nada, retorna valor en vez de guardarlo.
      set(value) {
        return value;
      }
    },
    showAmount() {
      if (this.checked || this.payment.isPending) return true;
      return false;
    },
    monetarySign() {
      if (this.$i18n.locale === 'cl') {
        return this.type === 'MORTGAGE' || this.type === 'SME' || this.type === 'PARKING' ? 'UF' : '$';
      }
      if (this.$i18n.locale === 'br') {
        return 'R$';
      }
      if (this.$i18n.locale === 'pe') {
        return 'S/';
      }
      return '';
    }
  },
  methods: {
    formatNumber: number => formatNumber({ number }),
    dateToClientFormat: date => dateToClientFormat(date),
    emitChangeOrConfirmDeselectPayment() {
      const inputEvent = {
        checked: !this.isChecked,
        selection: [
          {
            type: this.type,
            paymentNumber: this.payment.paymentNumber,
            operationId: this.payment.operationId
          }
        ]
      };
      // Emito el onChange para que select.vue seleccione la cuota
      if (!this.hasSiblings) {
        this.$emit('change', inputEvent);
        return;
      }
      // Si no, levanto una señal para que aparezca el modal
      this.$emit('confirm-deselect-payment', inputEvent);
    },
    confirmCheck() {
      // Si es backoffice, muestra cuadro de diálogo para preguntar si está seguro(a) de procesar o eliminar la cuota a la transacción
      if (!this.currentUserIsClient) {
        const confirmAction = this.isChecked ? 'eliminar' : 'procesar';
        this.$buefy.dialog.confirm({
          title: 'Importante',
          container: 'fixed',
          message: `¿Estás seguro(a) que deseas ${confirmAction} la cuota a la transacción?`,
          confirmText: 'Sí',
          cancelText: 'Cancelar',
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () => this.emitChangeOrConfirmDeselectPayment()
        });
      } else {
        // Si es cliente, procesa la transacción sin mostrar cuadro de diálogo
        this.emitChangeOrConfirmDeselectPayment();
      }
    }
  }
};
</script>

<style scoped>
.card-item {
  overflow: unset;
}
.noDoubleShadow {
  box-shadow: none;
}
.big-size-text {
  font-size: 1.2rem;
}
.med-size-text {
  font-size: 0.9rem;
}
.small-size-text {
  font-size: 0.75rem;
}
.tag {
  margin-left: 3px;
}
.tag.ninety-days {
  background-color: var(--secondary-dark);
}
.tag.sixty-days {
  background-color: var(--secondary);
}
.tag.thirty-days {
  background-color: orangered;
}
.tag.less-than-thirty-days {
  background-color: var(--yellow);
}
.tag.healthy-days {
  background-color: var(--primary);
}

/* item cuota - cabecera */
.fee-item-info {
  display: grid;
  grid-template-columns: minmax(auto, 1fr) 2rem;
  gap: 0% 5%;
  place-items: center;
  place-content: center stretch;
}
.fee-item-info.client {
  grid-template-columns: minmax(auto, 1.7rem) minmax(auto, 1fr) minmax(auto, 1.5rem);
}

.round-choice-transparent {
  border: 2px solid transparent;
}
.round-choice-content.payment-is-pending {
  border: 2px solid var(--yellow);
}
.round-choice-content.payment-is-pending input.input-choice:disabled ~ label.label-choice::before {
  background-color: var(--yellow);
}

.fee-item-info .fee-item-resume-content {
  width: 100%;
  display: grid;
  grid:
    "info1 info2 info3 info4" auto
    / 4rem minmax(max-content, 1fr) minmax(auto, 1.5fr) minmax(auto, 0.5fr);
  gap: 0% 5%;
  place-items: center;
  place-content: center stretch;
}
.client .fee-item-resume-content {
  grid:
    "info1 info2 info3" auto
    / 2rem minmax(max-content, 1fr) minmax(auto, 1.5fr);
}

.fee-item-info .fee-item-resume-content .type-content { grid-area: info1; }
.fee-item-info .fee-item-resume-content .price-content { grid-area: info2; }
.fee-item-info .fee-item-resume-content .duration-content { grid-area: info3; }
.fee-item-info .fee-item-resume-content .late-days-content { grid-area: info4; }

.content-title {
  color: var(--primary);
  font-weight: 600;
}
.alert-content-title {
  color: var(--secondary);
  font-weight: 600;
}

.fee-item-resume-content .price-content .show-fee-label {
  border-radius: 1rem;;
  padding: 0.5rem 1rem;
  background: #F2FBFB;
}

.fee-item-info .duration-content .fa-exclamation-triangle,
table .significant-data {
  color: var(--secondary);
}

.fa-exclamation-triangle {
  margin-right: 0.5rem;
}

.fee-item-resume-content .duration-content {
  display: flex;
  flex-flow: column wrap;
  justify-content: space-evenly;
  align-items: center;
}
.fee-item-info .details-content,
.fee-item-resume-content .duration-content div {
  display: flex;
  flex-flow: row wrap;
  justify-content: space-evenly;
}

.fee-item-resume-content .duration-content div span {
  margin-right: 0.3rem;
}

@media (max-width: 1000px) {
  .big-size-text {
    font-size: 1rem;
  }

  .med-size-text {
    font-size: 0.75rem;
  }

  .small-size-text {
    font-size: 0.65rem;
  }
}

@media (max-width: 900px) {
  .big-size-text {
    font-size: 1.2rem;
  }

  .med-size-text {
    font-size: 0.9rem;
  }

  .small-size-text {
    font-size: 0.75rem;
  }

  .duration-content .content-title,
  .duration-content .content-value {
    display: inline-block;
  }
}

@media (max-width: 660px) {
  .fee-item-info .fee-item-resume-content {
    grid:
      "info1" auto
      "info2" auto
      "info3" auto
      "info4" auto
      / 1fr;
    place-items: center;
  }
  .client .fee-item-resume-content {
    grid:
      "info1" auto
      "info2" auto
      "info3" auto
      /1fr;
    place-items: center;
  }
}

@media (max-width: 480px) {
  .fee-item-info {
    border-radius: 1.5rem;
  }
}

@media (max-width: 380px) {
  .big-size-text {
    font-size: 1rem;
  }

  .med-size-text {
    font-size: 0.75rem;
  }

  .small-size-text {
    font-size: 0.65rem;
  }
}
</style>

<i18n>
  {
    "cl": {
      "fee": "Cuota:",
      "expired": "Venció:",
      "expires": "Vence:",
      "credit": "Crédito",
      "mortgage": "hipotecario",
      "complementary": "de consumo",
      "sme": "PYME",
      "parking": "",
      "lateDays": "Días de mora"
    },
    "pe": {
      "fee": "Cuota:",
      "expired": "Venció:",
      "expires": "Vence:",
      "credit": "Crédito",
      "mortgage": "hipotecario",
      "sme": "PYME",
      "parking": "",
      "complementary": "de consumo",
      "lateDays": "Días de mora"
    },
    "br": {
      "fee": "Parcela:",
      "expired": "Vencimento:",
      "expires": "Vencimento:",
      "credit": "Contrato",
      "mortgage": "de Financiamento Imobiliário",
      "sme": "PYME",
      "parking": "Estacionamento",
      "complementary": "de consumo",
      "lateDays": "Dias em atraso"
    }
  }
</i18n>
